import type { ClientItem } from '@mimir/common/dto/searchResultHit';
import type { HighresDeletion } from '@mimir/common/dto/itemPropTypes';
import type { ExtendedItemDto } from '@mimir/common/dto/extendedItemDto';
import type { RouterLink } from '@mimir/common/dto/searchModels';
import type { LocalSegmentedMediaState } from '@mimir/common/util/mediaState';
import type { FolderPathSegmentDto } from '@mimir/common/dto/folderModels';
import type { AnalyticConfig } from '@mimir/common/dto/tenantModel';
import type { CoreNleClipInfo } from '@mimir/primitive/nle/projectItem';

export interface DependenciesUploadStatus {
  totalDependencies: number;
  doneDependencies: number;
  isUploading: boolean;
  totalUploadSize: number;
  progressPercentage: number;
}

/*
  Runtime typeinfo about a specific subsets of the Item fields that we need to
  retrieve from the server to perform various operations.

  By declaring the fields to retrieve this way, we can derive both the runtime
  field list as well as the compile time subset type of Item without needing to
  enumerate the fields more than once.

  These fields are fetched both for individual items as well as with the batch API. Therefore,
  some fields like virtualClipInfo cannot be added to this list since those are not supported
  to be fetched with the batch API. virtualClipInfo is not needed for syncing, since the original
  item ID is used for fetching the origin clip.
*/
export const syncItemFieldList = <const>[
  'id',
  'originalFileName',
  'folderParents',
  'archiveInfo',
  'mediaSize',
  'editProxyFormat',
  'metadata',
  'itemState',
  'media',
  'itemType',
  'visibleTo',
  'createdBy',
  'proxySize',
  'ingestSourceFullPath',
  'ingest',
  'highresDeletion',
  'isDeleting',
  'timecode',
  'mediaFrameRate',
  'technicalMetadata',
  'transcodingState',
  'proxy',
];
export type SyncItemInfo = Pick<ClientItem, typeof syncItemFieldList[number]>;


/**
 * This object describes information about a highres or proxy.
 *
 * TODO All properties are optional to allow initializing values
 *      of store.getters.localMediaStates with empty objects that
 *      are populated later. We should change this implementation
 *      to make it possible to have non optional properties here.
 */
export interface LocalMediaState {

  /**
   * TODO The meaning of this property changes overtime. We should sanitize this.
   *
   * In processPremiereClipInfos(), when constructing a NleClipInfo
   * from a CoreNleClipInfo, we construct 2 local LocalMediaState objects,
   * one for the highres, one for the proxy. At that point, the value of the
   * localPath property is either a path to the file as known in the Premiere clip
   * or an empty string if there is no path defined, for instance because we are in
   * highres mode and have no proxy.
   *
   * Note that when we are in proxy mode and there is no highres, the CoreNleClipInfo
   * object contains the proxy path in both its mediaPath and proxyPath properties. This
   * is normalized in processPremiereClipInfos() to turn the highres path into an empty string.
   *
   * However, this property may also be set by calling getItemArtefactLocalMediaState(), in which
   * case it always contain a non empty string pointing to where the file is expected to be.
   */
  localPath?: string;

  /**
   * If defined, this property indicates whether or not the path specified by localPath
   * actually points to an existing file.
   */
  isAvailableLocally?: boolean;

  /**
   * This property is used to disable the download UI widgets if a download is already in progress.
   */
  isBeingDownloaded?: boolean;

  /**
   * This property is to set to true when we start to check the local state and set to false
   * once we are done. It is used to know if we should disoplay an 'access_time' icon in the
   * Premiere sync page.
   */
  isStateRefreshing?: boolean;

  /**
   * The file size is used for 2 things:
   * - if the Premiere clip is not a Mimir item, we need to upload it and in that case,
   *   if the item deduplication feature flag is enabled, we use the size to try to
   *   find an existing Mimir item
   * - to report how much data going to be transferred if the item needs to be uploaded
   */
  fileSize?: number;

  /**
   * Describes whether or the item artefact is segmented and how.
   */
  segmentedMediaState?: LocalSegmentedMediaState;

}


// Path info stores information about local path on the disk for an item.
export interface LocalPathInfo {
  highresLinkPath?: string;
  proxyLinkPath?: string;
  aafLinkPath?: string;
  ancestorPaths?: FolderPathSegmentDto[][];
}

/**
 * This object contains all the information about a Nle clip,
 * both data obtained from the NleOps and additional information
 * needed by our UI.
 */
export interface NleClipInfo extends CoreNleClipInfo {

  /**
   * The local states of the highres and proxy.
   */
  mimirItemLocalMediaStates: ItemLocalMediaStates;

  /**
   * The Mimir item, if any, fetched from the backend.
   */
  mimirItem: NleMimirItemInfo | null;

  /**
   * This property is set to a non empty string when we fail
   * to fetch the Mimir item.
   */
  error?: string;

  /**
   * TODO This property can be removed since its value is derived from
   *      isProxyAvailableLocally and testing that the highres media path
   *      points to a .aegraphic file
   */
  preferMediaPathInProject?: boolean;

  /**
   * This property is set to true by the UI when the clip is
   * visible in the Nle sync page.
   */
  needsRefreshingLocalMediaStates?: boolean;

  /**
   * This property is used to disable the relink UI widget if a relink is already in progress.
   */
  isRelinking?: boolean;

  /**
   * This property is used to report an error to user when they are not allowed to access a Mimir
   * item used in a Premiere project.
   */
  isNotVisibleToUser?: boolean;

  /**
   * This property is used to report an error to user when they a Mimir item
   * is in the recycle bin.
   */
  isInRecycleBin?: boolean;

  /**
   * This property specifies the analytics configuration for the clip,
   * which will be used to perform analytics on it.
   */
  analyticConfig?: AnalyticConfig;
}

export interface ItemLocalMediaStates {
  highresState?: LocalMediaState;
  proxyState?: LocalMediaState;
}

export type NleClipInfoMap = Record<string, NleClipInfo>;


export interface NleMimirItemInfo
  extends Pick<
    ExtendedItemDto,
    | 'id'
    | 'originalFileName'
    | 'itemType'
    | 'archiveInfo'
    | 'mediaSize'
    | 'proxySize'
    | 'visibleTo'
    | 'createdBy'
    | 'ingestSourceFullPath'
    | 'ingest'
    | 'editProxyFormat'
    | 'itemState'
    | 'media'
    | 'isDeleting'
    | 'transcodingState'
    | 'proxy'
  > {
  link?: Partial<RouterLink>;
  highresDeletion?: Pick<HighresDeletion, 'deletionState' | 'initiatedBy'>;
}

export interface PrprojUploadStatus {
  message?: string;
}


/**
 * Interface that maps a kelda relative path to a locally accessible path
 */
export interface KeldaPathMapping {
  keldaId: string;
  type: 'indexed' | 'live' | 'primary-root';
  keldaRelativePathToFolder: string;
  userDefinedPathToFolder: string;
}
